import { useEffect, useState, type ReactNode } from 'react';
import { Box, Button, Flex, HStack, Stack, Text, useBreakpointValue } from '@chakra-ui/react';
import type { Bridge, HostInfo } from '@casa/bridge';
import { bridge } from '../bridge/native_bridge.ts';
import { readAppSettings } from '../data/app_settings.ts';
import { cloudLink, myPublicKey, readCloudKeys } from '../data/cloud_secrets.ts';
import { describeSource, readSource, requestReload } from '../data/dashboard.ts';
import { availableStores, readStoreSettings } from '../data/stores.ts';
import { appVersion } from '../diagnostics/app_version.ts';
import { DriversView } from '../drivers/drivers_page.tsx';
import { IconGlyph } from '../ui/icon_glyph.tsx';
import { AppPart } from './app_part.tsx';
import { DashboardsPart } from './dashboards_part.tsx';
import { DiagnosticsPart } from './diagnostics_part.tsx';
import { SecurityPart } from './security_part.tsx';
import { backInSettings, closeSettings, goToSettings, type SettingsPart } from './settings_navigation.ts';
import { SettingsGroup, SettingsRow } from './settings_ui.tsx';
import { StorePart } from './store_part.tsx';

// The settings, as an app's (docs/specs/settings.md §4.1): over the dashboard, which keeps running
// behind them (UI-2); a list of parts, each opening its own page; on a tablet or a computer the list
// on the left and the part on the right. "Done" goes back to the dashboard.

/** The shell's own version beside the app's (app-updates AU-28): they differ once the app is updated from tilez-cloud. */
const shellVersion = (globalThis as { casaHost?: HostInfo }).casaHost?.shellVersion;

const TITLES: Record<SettingsPart, string> = {
    store: 'Dashboard Store',
    dashboards: 'Dashboards',
    security: 'Tilez Cloud',
    app: 'App settings',
    drivers: 'Drivers',
    diagnostics: 'Diagnostics',
};

/** The one-line summaries in the list of parts. */
interface Summaries {
    store?: string | undefined;
    dashboards?: string | undefined;
    /** The dashboard shown, to reload; none without one. */
    reload?: string | undefined;
    app?: string | undefined;
    /** While paired with Tilez Cloud, whether its credentials are encrypted and this device has the key; none otherwise. */
    security?: string | undefined;
}

async function summaries(shell: Bridge) : Promise<Summaries> {
    const settings = await readStoreSettings(shell);
    const store = (await availableStores(shell)).find((entry) => entry.kind === settings.current);
    const source = await readSource(shell);
    const app = await readAppSettings(shell);
    // Whatever store is current: Tilez Cloud's credentials and devices are looked after from any (ST-10).
    const security = await securitySummary(shell).catch(() => undefined);

    return {
        store: store ? `${store.title}${store.detail ? ` · ${store.detail}` : ''}` : undefined,
        dashboards: source ? `Showing ${describeSource(source)}` : 'None yet',
        reload: source ? describeSource(source) : undefined,
        app: [
            app.keepAwake ? 'Keep awake on' : 'Keep awake off',
            app.keepAwake && app.sleep.enabled ? `asleep ${app.sleep.from}–${app.sleep.to}` : undefined,
            app.keepAwake && app.dim.enabled ? `dims after ${app.dim.minutes} min` : undefined,
        ].filter((part) => part !== undefined).join(' · '),
        security,
    };
}

/** "Your home" or "Waiting to be let in", for the list of parts (cloud-joining JN-1); undefined when this device is not on Tilez Cloud. */
async function securitySummary(shell: Bridge) : Promise<string | undefined> {
    const link = await cloudLink(shell);

    if (!link) {
        return undefined;
    }

    const keys = await readCloudKeys(shell, link);

    if (keys.keyVersion === 0) {
        return 'Passwords not protected yet';
    }

    const publicKey = await myPublicKey();

    return keys.mine && keys.devices.some((device) => device.id === keys.me && device.publicKey === publicKey) ? 'Your home' : 'Waiting to be let in';
}

function PartView({ shell, part, onChanged }: { shell: Bridge; part: SettingsPart; onChanged: () => void }) : ReactNode {
    switch (part) {
        case 'store':
            return <StorePart shell={shell} onChanged={onChanged} />;
        case 'dashboards':
            return <DashboardsPart shell={shell} onChanged={onChanged} />;
        case 'security':
            return <SecurityPart shell={shell} onChanged={onChanged} />;
        case 'app':
            return <AppPart shell={shell} onChanged={onChanged} />;
        case 'drivers':
            return <DriversView shell={shell} />;
        case 'diagnostics':
            return <DiagnosticsPart shell={shell} />;
    }
}

export function SettingsApp({ route }: { route: SettingsPart | '' }) : ReactNode {
    const isWide = useBreakpointValue({ base: false, md: true }, { ssr: false }) ?? false;
    const [info, setInfo] = useState<Summaries>({});
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        if (bridge) {
            void summaries(bridge).then(setInfo);
        }
    }, [revision, route]);

    // On a wide screen a part is always shown beside the list: the dashboards, to begin with.
    const part: SettingsPart | '' = route === '' && isWide ? 'dashboards' : route;
    // The drivers are a part of the app settings.
    const listed: SettingsPart | '' = part === 'drivers' ? 'app' : part;
    const onChanged = () : void => setRevision((value) => value + 1);

    const list = (
        <Stack gap="6">
            {/* An action, not a setting: a button above the parts; the settings close and the dashboard is read again (DB-8, issue #26). */}
            {info.reload ? (
                <Button
                    variant="outline"
                    colorPalette="blue"
                    width="full"
                    onClick={() => {
                        requestReload();
                        closeSettings();
                    }}
                    data-testid="reload-dashboard"
                >
                    <IconGlyph name="arrows-rotate" size={16} color="#60a5fa" />
                    <Text truncate>Reload dashboard · {info.reload}</Text>
                </Button>
            ) : null}
            <SettingsGroup>
                <SettingsRow icon="database" title={TITLES.store} detail={info.store} isSelected={isWide && listed === 'store'} onClick={() => goToSettings('store')} data-testid="part-store" />
                <SettingsRow
                    icon="table-cells-large"
                    title={TITLES.dashboards}
                    detail={info.dashboards}
                    isSelected={isWide && listed === 'dashboards'}
                    onClick={() => goToSettings('dashboards')}
                    data-testid="part-dashboards"
                />
                {info.security ? (
                    <SettingsRow icon="cloud" title={TITLES.security} detail={info.security} isSelected={isWide && listed === 'security'} onClick={() => goToSettings('security')} data-testid="part-security" />
                ) : null}
            </SettingsGroup>
            <SettingsGroup>
                <SettingsRow icon="sliders" title={TITLES.app} detail={info.app} isSelected={isWide && listed === 'app'} onClick={() => goToSettings('app')} data-testid="part-app" />
                <SettingsRow icon="stethoscope" title={TITLES.diagnostics} isSelected={isWide && listed === 'diagnostics'} onClick={() => goToSettings('diagnostics')} data-testid="part-diagnostics" />
            </SettingsGroup>
            {/* For a tester's report (DI-3): selectable, to copy. */}
            <Text fontSize="sm" color="fg.muted" textAlign="center" userSelect="text" data-testid="app-version">
                Tilez {appVersion}
                {shellVersion ? ` (app ${shellVersion})` : ''}
            </Text>
        </Stack>
    );

    const content =
        part === '' || !bridge ? null : (
            <Stack gap="4">
                <Text fontSize="2xl" fontWeight="bold">
                    {TITLES[part]}
                </Text>
                <PartView key={part} shell={bridge} part={part} onChanged={onChanged} />
            </Stack>
        );

    const showsBack = !isWide && part !== '';

    return (
        <Flex
            className="chrome"
            position="fixed"
            inset="0"
            zIndex="900"
            direction="column"
            bg="bg"
            color="fg"
            // Below the status bar, and below the window's buttons in the Electron shell on Windows (index.css, issue #28).
            pt="var(--casa-top-inset)"
            pb="env(safe-area-inset-bottom)"
            ps="env(safe-area-inset-left)"
            pe="env(safe-area-inset-right)"
            data-testid="settings"
        >
            <HStack px={{ base: '3', md: '5' }} py="2" borderBottomWidth="1px" borderColor="border.muted" minH="14">
                {showsBack ? (
                    <Button variant="ghost" size="sm" px="1" color="blue.400" onClick={backInSettings} aria-label="Back to the settings">
                        <Box transform="rotate(180deg)">
                            <IconGlyph name="chevron-right" size={14} color="#60a5fa" />
                        </Box>
                        Settings
                    </Button>
                ) : (
                    <Text fontSize="lg" fontWeight="semibold">
                        Settings
                    </Text>
                )}
                <Box flex="1" />
                <Button variant="ghost" size="sm" color="blue.400" fontWeight="semibold" onClick={closeSettings} data-testid="settings-done">
                    Done
                </Button>
            </HStack>
            {isWide ? (
                <Flex flex="1" minH="0">
                    <Box width="80" flex="none" overflowY="auto" overscrollBehavior="none" p="4" borderEndWidth="1px" borderColor="border.muted">
                        {list}
                    </Box>
                    <Box flex="1" minW="0" overflowY="auto" overscrollBehavior="none" p="6">
                        <Box maxW="3xl">{content}</Box>
                    </Box>
                </Flex>
            ) : (
                <Box flex="1" minH="0" overflowY="auto" overscrollBehavior="none" p="4">
                    {part === '' ? list : content}
                </Box>
            )}
        </Flex>
    );
}
